// client/src/dnd/emitter_tool/engine/engine_parts/targeting/OriginDirection.js
export default function installOriginDirection(env) {
  const WIN = typeof window !== "undefined" ? window : null;
  const GLOBAL_KEY = "__ET_OriginDirectionSingleton";
  try {
    const prev = WIN && WIN[GLOBAL_KEY];
    if (prev && typeof prev.destroy === "function") prev.destroy();
  } catch {}

  const canvas = env?.canvas || null;
  const ctx = env?.ctx || null;
  const getOrigin = typeof env?.getOrigin === "function" ? env.getOrigin : () => ({ x: 0, y: 0 });
  const dispatch = typeof env?.dispatch === "function" ? env.dispatch : (n, d) => { try { WIN?.dispatchEvent(new CustomEvent(n, { detail: d })); } catch {} };

  if (!canvas || !ctx) {
    const api = { destroy(){}, draw(){}, pickStart(){}, onPointerMove(){}, onPointerEnter(){}, onPointerLeave(){}, onClick(){} };
    if (WIN) WIN[GLOBAL_KEY] = api;
    return api;
  }

  const host = canvas.parentElement;
  const prevPointer = canvas.style.pointerEvents;
  canvas.style.pointerEvents = "none";

  const DPR = () => Math.max(1, (WIN?.devicePixelRatio) || 1);
  const softClear = () => ctx.clearRect(0, 0, canvas.width, canvas.height);

  const UI_FIRE_MODE = "EmitterTool:UI:fireMode";
  const UI_PICKED_X = "EmitterTool:UI:pickedOriginX";
  const UI_PICKED_Y = "EmitterTool:UI:pickedOriginY";
  const UI_PICKED_A = "EmitterTool:UI:pickedAngleDeg";
  const UI_LAST_A  = "EmitterTool:UI:lastAngleDeg";

  const LS_MOUSE_X = "EmitterTool:UI:lastMouseXCss";
  const LS_MOUSE_Y = "EmitterTool:UI:lastMouseYCss";

  let lastMouseCss = null;
  let lastClient = null;
  let alive = true;
  let rafId = 0;

  function isInSidebarTarget(t) {
    try { return !!(t && typeof t.closest === "function" && t.closest('[data-et-ui="sidebar"]')); } catch { return false; }
  }
  function isInsideHost(e) {
    if (!host) return false;
    const r = host.getBoundingClientRect();
    const x = e?.clientX ?? 0, y = e?.clientY ?? 0;
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }
  function toHostCssPoint(e) {
    const r = host.getBoundingClientRect();
    return { xCss: Math.max(0, Math.min(r.width, e.clientX - r.left)), yCss: Math.max(0, Math.min(r.height, e.clientY - r.top)), clientX: e.clientX, clientY: e.clientY };
  }

  const sendOverlay = (tokenCss, mouseCss) => {
    const center = mouseCss ? { xCss: mouseCss.xCss, yCss: mouseCss.yCss, clientX: mouseCss.clientX ?? null, clientY: mouseCss.clientY ?? null }
                            : { xCss: tokenCss.x, yCss: tokenCss.y };
    try {
      WIN?.dispatchEvent(new CustomEvent("EmitterTool:UI:EmitterOverlayConfig", {
        detail: {
          show: true,
          settings: { centerOn: "token", lockCenterOn: true },
          origin: { x: tokenCss.x, y: tokenCss.y, xCss: tokenCss.x, yCss: tokenCss.y },
          center
        }
      }));
    } catch {}
  };

  (function restore() {
    const rect = host.getBoundingClientRect();
    const xRaw = Number(localStorage.getItem(LS_MOUSE_X));
    const yRaw = Number(localStorage.getItem(LS_MOUSE_Y));
    if (Number.isFinite(xRaw) && Number.isFinite(yRaw)) {
      const xCss = Math.max(0, Math.min(rect.width, xRaw));
      const yCss = Math.max(0, Math.min(rect.height, yRaw));
      lastMouseCss = { xCss, yCss };
    }
    const tokenCss = getOrigin() || { x: 0, y: 0 };
    sendOverlay(tokenCss, lastMouseCss);
  })();

  function renderOnce() {
    const r = DPR();
    const pr = host.getBoundingClientRect();
    const w = Math.max(1, Math.floor(pr.width * r));
    const h = Math.max(1, Math.floor(pr.height * r));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      canvas.style.width = pr.width + "px";
      canvas.style.height = pr.height + "px";
    }
    softClear();
    const tokenCss = getOrigin() || { x: 0, y: 0 };
    sendOverlay(tokenCss, lastMouseCss);
  }

  function loop() {
    if (!alive) return;
    rafId = requestAnimationFrame(loop);
    renderOnce();
  }

  const computeAngleDeg = (fromCss, toCss) => {
    const dx = toCss.xCss - fromCss.x;
    const dy = toCss.yCss - fromCss.y;
    return (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
  };

  const updateMouse = (e) => {
    if (!isInsideHost(e) || isInSidebarTarget(e?.target)) return;
    const p = toHostCssPoint(e);
    lastMouseCss = { xCss: p.xCss, yCss: p.yCss, clientX: p.clientX, clientY: p.clientY };
    try { localStorage.setItem(LS_MOUSE_X, String(p.xCss)); } catch {}
    try { localStorage.setItem(LS_MOUSE_Y, String(p.yCss)); } catch {}
  };

  const onClick = (e) => {
    if (!isInsideHost(e) || isInSidebarTarget(e?.target)) return;
    const clickCss = toHostCssPoint(e);
    const tokenCss = getOrigin() || { x: 0, y: 0 };
    const angleDeg = computeAngleDeg(tokenCss, clickCss);
    try {
      localStorage.setItem(UI_PICKED_X, String(tokenCss.x));
      localStorage.setItem(UI_PICKED_Y, String(tokenCss.y));
      localStorage.setItem(UI_PICKED_A, String(angleDeg));
      localStorage.setItem(UI_LAST_A, String(angleDeg));
      localStorage.setItem(UI_FIRE_MODE, "rule");
    } catch {}
    try { WIN?.dispatchEvent(new CustomEvent("EmitterTool:RulePhaseStart")); } catch {}
    const payload = { x: tokenCss.x, y: tokenCss.y, angleDeg, angle: angleDeg, rotationDeg: angleDeg, addRotDeg: angleDeg, shapeSettings: { rotationDeg: angleDeg }, effectSettings: { rotationDeg: angleDeg } };
    dispatch("EmitterTool:UI:PickedLocation", payload);
    dispatch("EmitterTool:UI:FireRule", payload);
  };

  const bind = () => {
    WIN?.addEventListener?.("pointermove", updateMouse, { passive: true, capture: true });
    WIN?.addEventListener?.("resize", renderOnce);
    WIN?.addEventListener?.("click", onClick, { capture: true });
  };

  const unbind = () => {
    WIN?.removeEventListener?.("pointermove", updateMouse, { capture: true });
    WIN?.removeEventListener?.("resize", renderOnce);
    WIN?.removeEventListener?.("click", onClick, { capture: true });
  };

  bind();
  loop();
  renderOnce();

  const api = {
    destroy() {
      alive = false;
      if (rafId) cancelAnimationFrame(rafId);
      unbind();
      softClear();
      canvas.style.pointerEvents = prevPointer ?? "";
      if (WIN && WIN[GLOBAL_KEY] === api) WIN[GLOBAL_KEY] = null;
      try { WIN?.dispatchEvent(new CustomEvent("EmitterTool:UI:EmitterOverlayConfig", { detail: { settings: { lockCenterOn: false } } })); } catch {}
    },
    draw() {},
    pickStart() {},
    onPointerMove() {},
    onPointerEnter() {},
    onPointerLeave() {},
    onClick() {},
  };

  if (WIN) WIN[GLOBAL_KEY] = api;
  return api;
}
